import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useEditMode,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

/**
 * Sister Properties – curated nearby communities under the same management.
 *
 * Company theme: leave community slots empty. There is no portfolio collection
 * in Multihub, so content is filled per property site.
 *
 * Property theme / Generate: pull sister communities from this property’s
 * current live website (`property_website_url` in MH). Prefer a Sister
 * Properties / Our Communities page. Cap at 6 — if more exist, use the
 * geographically closest six. For each filled card, also select that sister’s
 * listing photo (and write descriptive alt text). Addresses must be full street
 * addresses and link out (maps URL). If a URL is provided in the property-level
 * request instead, use that page as the source.
 *
 * Empty slots stay hidden on the live site and show as placeholders in edit mode.
 *
 * Listing photos: matched at render time from the live Stonebriar sister-
 * properties catalog by community name, so each card uses that property’s
 * own photo rather than a positional “closest six” guess.
 */

/** Listing thumbs from https://www.stonebriarkc.com/sisterproperties */
const SISTER_LISTING_PHOTOS: ReadonlyArray<{ name: string; image: string; alt: string }> = [
  {
    name: 'Stonebriar Woods Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/3sbw-amenities-4_thumbnail.jpg',
    alt: 'Exterior amenities and grounds at Stonebriar Woods Apartment Homes',
  },
  {
    name: 'Creekside Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124089/ks_overlandpark_creekside_p1099041_22_5_thumbnail(1).jpg',
    alt: 'Apartment building exterior at Creekside Apartment Homes',
  },
  {
    name: 'Crowne Chase Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124097/ks_overlandpark_crownechase_p1099049_07_5_thumbnail(1).jpg',
    alt: 'Apartment building exterior at Crowne Chase Apartment Homes',
  },
  {
    name: 'Stonepost Ranch Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/6spr-amenities-4_thumbnail.jpg',
    alt: 'Community amenity area at Stonepost Ranch Apartment Homes',
  },
  {
    name: 'Avignon Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/1avg-amenities-1_thumbnail.jpg',
    alt: 'Pool and amenity area at Avignon Apartment Homes',
  },
  {
    name: 'Stonepost Lakeside Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/5spl-amenities-12_thumbnail.jpg',
    alt: 'Community grounds and amenities at Stonepost Lakeside Apartment Homes',
  },
  {
    name: 'The Claremont Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/1cl-amenities-1(2)_thumbnail.jpg',
    alt: 'Apartment building exterior at The Claremont Apartment Homes',
  },
  {
    name: 'Waterford Place Apartments & Townhomes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/ks_overlandpark_waterfordplace_p1099356_2_5_thumbnail.jpg',
    alt: 'Apartment building exterior at Waterford Place Apartments & Townhomes',
  },
  {
    name: 'Sorrento at Deer Creek Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/ks_overlandpark_sorrentoatdeercreek_p1099140_4_03_1_communitythumbnai_1.jpg',
    alt: 'Community exterior at Sorrento at Deer Creek Apartment Homes',
  },
  {
    name: 'Somerset Oaks Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/4so_amenities_1_thumbnail.jpg',
    alt: 'Community amenity area at Somerset Oaks Apartment Homes',
  },
  {
    name: 'Deer Creek Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124099/ks_overlandpark_deercreek_p1099051_02_5_thumbnail.jpg',
    alt: 'Apartment building exterior at Deer Creek Apartment Homes',
  },
  {
    name: 'Highland Park Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/3hp-amenities-1(2)_thumbnail.jpg',
    alt: 'Apartment building exterior at Highland Park Apartment Homes',
  },
  {
    name: 'Wynnewood Farms',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124410/ks_overlandpark_wynnewoodfarms_p1099361_26_5_thumbnail.jpg',
    alt: 'Apartment building exterior at Wynnewood Farms',
  },
  {
    name: 'Millcreek Woods Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/3mw_amenities_18_thumbnail.jpg',
    alt: 'Community amenity area at Millcreek Woods Apartment Homes',
  },
  {
    name: 'Pebblebrook Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/4pb-amenities-12_thumbnail.jpg',
    alt: 'Community amenity area at Pebblebrook Apartment Homes',
  },
  {
    name: 'Wind River Lodge Apartments & Townhomes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/5wrl_amenities_2_thumbnail.jpg',
    alt: 'Community amenity area at Wind River Lodge Apartments & Townhomes',
  },
  {
    name: 'Louisburg Square Apartments & Townhomes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124170/ks_overlandpark_louisburgsquare_p1099122_01_5_thumbnail.jpg',
    alt: 'Apartment building exterior at Louisburg Square Apartments & Townhomes',
  },
  {
    name: 'Prairie Creek Apartments & Townhomes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/prairie-creek-019_thumbnail.jpg',
    alt: 'Community exterior at Prairie Creek Apartments & Townhomes',
  },
  {
    name: 'Sheridan Ridge Townhomes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/ks_overlandpark_sheridanridge_p1099135_1_5_thumbnail.jpg',
    alt: 'Townhome exterior at Sheridan Ridge Townhomes',
  },
  {
    name: 'Crescent Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124093/ks_lenexa_crescent_p1099045_thumbsapts247_5_thumbnail.jpg',
    alt: 'Apartment building exterior at Crescent Apartment Homes',
  },
  {
    name: 'Cloverset Active Adult Apartments',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/2cv-amenities-13_thumbnail.jpg',
    alt: 'Community amenity area at Cloverset Active Adult Apartments',
  },
  {
    name: 'Bremerton Park Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/ks_prairievillage_bremertonpark_p1099031_2_5_thumbnail.jpg',
    alt: 'Apartment building exterior at Bremerton Park Apartment Homes',
  },
  {
    name: 'The Residences at Bluhawk Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/img_1699-hdr-3_thumbnail.jpg',
    alt: 'Apartment building exterior at The Residences at Bluhawk',
  },
  {
    name: '46 Penn Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/mo_kansascity_46penn_p1098965_01_5_thumbnail.jpg',
    alt: 'Apartment building exterior at 46 Penn Apartment Homes',
  },
  {
    name: 'Cambria Luxury Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/mo_kansascity_cambria_p1099042_01_5_thumbnail-2.jpg',
    alt: 'Apartment building exterior at Cambria Luxury Apartment Homes',
  },
  {
    name: 'Malvern Hill Apartment Homes',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124172/ks_kansascity_malvernhill_p1099124_01_5_thumbnail.jpg',
    alt: 'Apartment building exterior at Malvern Hill Apartment Homes',
  },
  {
    name: 'West 39th Street',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/02-dsc01273_alt_final(2).jpg',
    alt: 'Apartment building exterior at West 39th Street',
  },
  {
    name: 'Sky On Main',
    image:
      'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/mo_kansascity_skyonmain_p1532313_39_5_thumbnail.jpg',
    alt: 'Apartment building exterior at Sky On Main',
  },
];

function normalizeCommunityKey(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\b(apartment|apartments|homes|home|townhomes|townhome|and|the|at|luxury|active|adult)\b/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function findSisterListing(name: string | null | undefined): { image: string; alt: string } | null {
  const raw = (name || '').trim();
  if (!raw) return null;
  const key = normalizeCommunityKey(raw);
  if (!key) return null;

  const exact = SISTER_LISTING_PHOTOS.find((entry) => normalizeCommunityKey(entry.name) === key);
  if (exact) return { image: exact.image, alt: exact.alt };

  // Shorter card titles ("Claremont", "Waterford Place"): pick the tightest
  // catalog match so "Deer Creek" does not steal Sorrento at Deer Creek.
  let best: { image: string; alt: string; score: number } | null = null;
  for (const entry of SISTER_LISTING_PHOTOS) {
    const entryKey = normalizeCommunityKey(entry.name);
    if (!entryKey.includes(key) && !key.includes(entryKey)) continue;
    const score = Math.min(entryKey.length, key.length) / Math.max(entryKey.length, key.length);
    if (!best || score > best.score) {
      best = { image: entry.image, alt: entry.alt, score };
    }
  }
  return best && best.score >= 0.45 ? { image: best.image, alt: best.alt } : null;
}

// ─── Slot group definitions (one literal per community — schema extractor) ─

const community1Def: SlotGroupDefinition = {
  label: 'Community 1',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/3sbw-amenities-4_thumbnail.jpg',
      generator: {
        prompt:
          'Photo of this sister community from its listing on the source Sister Properties / Our Communities page (or that community’s own site). Prefer the image shown beside that community on the source page — typically a building exterior or community hero.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict this card’s community only — not this property’s own gallery and not photos used on cards 2–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Exterior amenities and grounds at Stonebriar Woods Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Stonebriar Woods Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '12401 W. 120th St, Overland Park, KS 66213',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue:
        'https://www.google.com/maps/search/?api=1&query=12401%20W.%20120th%20St%2C%20Overland%20Park%2C%20KS%2066213',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.stonebriarwoods.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'A sister community listed on this property’s current live website (property_website_url in Multihub), usually on a Sister Properties or Our Communities page. Prefer the geographically closest sister to this property. When filling this card, always include its listing photo (image) plus alt text, name, full street address, a maps URL for that address, and the community website URL — do not leave the image empty if the community is filled. Do not invent communities that are not on the source site.',
    context:
      'Card 1 of up to 6 on Sister Properties. If fewer than 6 sisters exist, leave_empty. If more than 6, pick the closest unused one. Do not reuse cards 2–6. When a source URL was provided at the property theme level, use that page instead of guessing.',
  },
};

const community2Def: SlotGroupDefinition = {
  label: 'Community 2',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124089/ks_overlandpark_creekside_p1099041_22_5_thumbnail(1).jpg',
      generator: {
        prompt:
          'Photo of this sister community from its listing on the source Sister Properties / Our Communities page (or that community’s own site). Prefer the image shown beside that community on the source page — typically a building exterior or community hero.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict this card’s community only — not this property’s own gallery and not photos used on cards 1 or 3–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Apartment building exterior at Creekside Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Creekside Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '11920 Oakmont St, Overland Park, KS 66213',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue:
        'https://www.google.com/maps/search/?api=1&query=11920%20Oakmont%20St%2C%20Overland%20Park%2C%20KS%2066213',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.creeksideaptskc.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), geographically next-closest after card 1. When filling this card, always include its listing photo (image) plus alt text, name, full street address, maps URL, and community website — do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 2 of up to 6. Different community from cards 1, 3–6. leave_empty if no second sister exists. Prefer closest unused; use a provided property-level source URL when given.',
  },
};

const community3Def: SlotGroupDefinition = {
  label: 'Community 3',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//3/1124097/ks_overlandpark_crownechase_p1099049_07_5_thumbnail(1).jpg',
      generator: {
        prompt:
          'Photo of this sister community from its listing on the source Sister Properties / Our Communities page (or that community’s own site). Prefer the image shown beside that community on the source page — typically a building exterior or community hero.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict this card’s community only — not this property’s own gallery and not photos used on cards 1–2 or 4–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Apartment building exterior at Crowne Chase Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Crowne Chase Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '11621 W. 118th Terrace, Overland Park, KS 66210',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue:
        'https://www.google.com/maps/search/?api=1&query=11621%20W.%20118th%20Terrace%2C%20Overland%20Park%2C%20KS%2066210',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.crownechase.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), next-closest after cards 1–2. When filling this card, always include its listing photo (image) plus alt text, name, full street address, maps URL, and community website — do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 3 of up to 6. Different from cards 1–2 and 4–6. leave_empty if fewer than 3 sisters. Prefer closest unused; use a provided property-level source URL when given.',
  },
};

const community4Def: SlotGroupDefinition = {
  label: 'Community 4',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/6spr-amenities-4_thumbnail.jpg',
      generator: {
        prompt:
          'Photo of this sister community from its listing on the source Sister Properties / Our Communities page (or that community’s own site). Prefer the image shown beside that community on the source page — typically a building exterior or community hero.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict this card’s community only — not this property’s own gallery and not photos used on cards 1–3 or 5–6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Community amenity area at Stonepost Ranch Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Stonepost Ranch Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '12801 W. 136th Street, Overland Park, KS 66221',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue:
        'https://www.google.com/maps/search/?api=1&query=12801%20W.%20136th%20Street%2C%20Overland%20Park%2C%20KS%2066221',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.stonepostranchapts.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), next-closest after cards 1–3. When filling this card, always include its listing photo (image) plus alt text, name, full street address, maps URL, and community website — do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 4 of up to 6. Different from cards 1–3 and 5–6. leave_empty if fewer than 4 sisters. Prefer closest unused; use a provided property-level source URL when given.',
  },
};

const community5Def: SlotGroupDefinition = {
  label: 'Community 5',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/1avg-amenities-1_thumbnail.jpg',
      generator: {
        prompt:
          'Photo of this sister community from its listing on the source Sister Properties / Our Communities page (or that community’s own site). Prefer the image shown beside that community on the source page — typically a building exterior or community hero.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict this card’s community only — not this property’s own gallery and not photos used on cards 1–4 or 6.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Pool and amenity area at Avignon Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Avignon Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '11820 S Greenwood St., Olathe, KS 66062',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue:
        'https://www.google.com/maps/search/?api=1&query=11820%20S%20Greenwood%20St.%2C%20Olathe%2C%20KS%2066062',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: { label: 'Website URL', type: 'url', defaultValue: 'https://www.avignonapts.com/' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), next-closest after cards 1–4. When filling this card, always include its listing photo (image) plus alt text, name, full street address, maps URL, and community website — do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 5 of up to 6. Different from cards 1–4 and 6. leave_empty if fewer than 5 sisters. Prefer closest unused; use a provided property-level source URL when given.',
  },
};

const community6Def: SlotGroupDefinition = {
  label: 'Community 6',
  description:
    'Filled at the property level from the current MH website (or a provided URL). Leave empty on the company theme.',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        'https://resource.rentcafe.com/image/upload/q_auto,f_auto,c_fill,w_800,h_600,g_auto/s3//2/5458/5spl-amenities-12_thumbnail.jpg',
      generator: {
        prompt:
          'Photo of this sister community from its listing on the source Sister Properties / Our Communities page (or that community’s own site). Prefer the image shown beside that community on the source page — typically a building exterior or community hero.',
        context:
          'Card-thumb image in the Sister Properties grid (4:3). Must depict this card’s community only — not this property’s own gallery and not photos used on cards 1–5.',
      },
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Community grounds and amenities at Stonepost Lakeside Apartment Homes',
      generator: {
        prompt:
          'Descriptive alt text for this sister community’s photo — name what is visible (building exterior, amenity, street view) without marketing fluff.',
        length: '1 short sentence',
      },
    },
    name: { label: 'Community Name', type: 'text', defaultValue: 'Stonepost Lakeside Apartment Homes' },
    location: {
      label: 'Full Address',
      type: 'text',
      defaultValue: '13810 S. Pflumm Road, Olathe, KS 66062',
      description: 'Full street address (street, city, state, ZIP).',
    },
    addressUrl: {
      label: 'Address Map URL',
      type: 'url',
      defaultValue:
        'https://www.google.com/maps/search/?api=1&query=13810%20S.%20Pflumm%20Road%2C%20Olathe%2C%20KS%2066062',
      description: 'Google Maps (or similar) link for the full address — the address text links out here.',
    },
    url: {
      label: 'Website URL',
      type: 'url',
      defaultValue: 'https://www.stonepostlakesideapartments.com/',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Visit Community' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
  generator: {
    prompt:
      'Another unused sister community from this property’s current live website (property_website_url), next-closest after cards 1–5. When filling this card, always include its listing photo (image) plus alt text, name, full street address, maps URL, and community website — do not leave the image empty if the community is filled. Do not invent listings.',
    context:
      'Card 6 of up to 6. Different from cards 1–5. leave_empty if fewer than 6 sisters. Prefer closest unused; use a provided property-level source URL when given.',
  },
};

function hasText(value: string | null | undefined): boolean {
  return Boolean(value && value.trim());
}

function isFilled(community: Record<string, string | null>): boolean {
  return (
    hasText(community.name) ||
    hasText(community.url) ||
    hasText(community.image) ||
    hasText(community.location)
  );
}

function CommunityCardBody({
  community,
  number,
  isEditing,
  filled,
}: {
  community: Record<string, string | null>;
  number: number;
  isEditing: boolean;
  filled: boolean;
}) {
  const href = (community.url || '').trim();
  const name = (community.name || '').trim();
  const location = (community.location || '').trim();
  const addressHref = (community.addressUrl || '').trim();
  const linkLabel = (community.label || 'Visit Community').trim();
  const target = community._target || '_blank';
  const rel = target === '_blank' ? 'noopener noreferrer' : undefined;
  // Prefer listing photo for this card’s community name from the live
  // sister-properties page so positional defaults can’t attach the wrong media.
  // Prefer the editable slot alt so Alt Text is visible/editable in the editor.
  const listing = findSisterListing(name);
  const imageUrl = listing?.image || (community.image || '').trim();
  const imageAlt =
    (community.alt || '').trim() ||
    listing?.alt ||
    (name ? `${name} property image` : `Community ${number}`);

  return (
    <article
      className={`sister-card${filled ? '' : ' sister-card--empty'}`}
      aria-label={filled ? undefined : `Community ${number} — click to add details`}
    >
      <div className="sister-card-media">
        {hasText(imageUrl) ? (
          <VibesitesImage
            url={imageUrl}
            alt={imageAlt}
            className="sister-card-image"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
          />
        ) : (
          <div className="sister-card-image-placeholder" aria-hidden="true" />
        )}
      </div>

      <div className="sister-card-body">
        <h3 className="sister-card-name">
          {name || (isEditing ? `Community ${number}` : '')}
        </h3>
        {location ? (
          addressHref ? (
            <a
              className="sister-card-location sister-card-location--link"
              href={addressHref}
              target={target}
              rel={rel}
            >
              {location}
            </a>
          ) : (
            <p className="sister-card-location">{location}</p>
          )
        ) : isEditing && !filled ? (
          <p className="sister-card-location sister-card-hint">Add full street address</p>
        ) : null}

        {href ? (
          <a className="sister-card-link" href={href} target={target} rel={rel}>
            {linkLabel}
          </a>
        ) : isEditing ? (
          <span className="sister-card-link sister-card-link--muted">Add website URL</span>
        ) : null}
      </div>
    </article>
  );
}

export function SisterProperties() {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();
  const editMode = useEditMode();
  const isEditing = Boolean(editMode?.isEditMode);

  const community1 = useSlotGroup('sisterproperties.community1', community1Def);
  const community2 = useSlotGroup('sisterproperties.community2', community2Def);
  const community3 = useSlotGroup('sisterproperties.community3', community3Def);
  const community4 = useSlotGroup('sisterproperties.community4', community4Def);
  const community5 = useSlotGroup('sisterproperties.community5', community5Def);
  const community6 = useSlotGroup('sisterproperties.community6', community6Def);

  return (
    <section ref={sectionRef} className="reveal-fade-up sister-properties">
      <div className="sister-properties-container">
        <div className="sister-properties-grid">
          {/*
            EditableSlotGroup groupPath must be a string literal for the schema
            linter — keep each wrapper inline rather than mapping a shared path.
          */}
          {(isEditing || isFilled(community1)) && (
            <EditableSlotGroup groupPath="sisterproperties.community1" definition={community1Def}>
              <CommunityCardBody
                community={community1}
                number={1}
                isEditing={isEditing}
                filled={isFilled(community1)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community2)) && (
            <EditableSlotGroup groupPath="sisterproperties.community2" definition={community2Def}>
              <CommunityCardBody
                community={community2}
                number={2}
                isEditing={isEditing}
                filled={isFilled(community2)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community3)) && (
            <EditableSlotGroup groupPath="sisterproperties.community3" definition={community3Def}>
              <CommunityCardBody
                community={community3}
                number={3}
                isEditing={isEditing}
                filled={isFilled(community3)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community4)) && (
            <EditableSlotGroup groupPath="sisterproperties.community4" definition={community4Def}>
              <CommunityCardBody
                community={community4}
                number={4}
                isEditing={isEditing}
                filled={isFilled(community4)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community5)) && (
            <EditableSlotGroup groupPath="sisterproperties.community5" definition={community5Def}>
              <CommunityCardBody
                community={community5}
                number={5}
                isEditing={isEditing}
                filled={isFilled(community5)}
              />
            </EditableSlotGroup>
          )}
          {(isEditing || isFilled(community6)) && (
            <EditableSlotGroup groupPath="sisterproperties.community6" definition={community6Def}>
              <CommunityCardBody
                community={community6}
                number={6}
                isEditing={isEditing}
                filled={isFilled(community6)}
              />
            </EditableSlotGroup>
          )}
        </div>

        {isEditing ? (
          <p className="sister-properties-editor-note">
            Property level: generate or fill from the current Multihub website URL
            (or a provided sister-properties page). Use up to six closest communities;
            full addresses link out via Map URL. Empty cards stay hidden on the live site.
          </p>
        ) : null}
      </div>

      <style>{`
        .sister-properties {
          padding: 3.5rem 0 4rem;
          background: #ffffff;
        }
        @media (min-width: 768px) {
          .sister-properties { padding: 5rem 0 5.5rem; }
        }

        .sister-properties-container {
          max-width: 72rem;
          margin: 0 auto;
          padding: 0 1rem;
        }
        @media (min-width: 640px) { .sister-properties-container { padding: 0 1.5rem; } }
        @media (min-width: 1024px) { .sister-properties-container { padding: 0 2rem; } }

        .sister-properties-grid {
          display: grid;
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
        @media (min-width: 640px) {
          .sister-properties-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (min-width: 1024px) {
          .sister-properties-grid { grid-template-columns: repeat(3, 1fr); gap: 1.75rem; }
        }

        .sister-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          background: #ffffff;
          border: 1px solid #e8e8ea;
          border-radius: var(--vibe-radius-lg, 12px);
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .sister-card:hover {
          box-shadow: 0 8px 28px rgba(0, 0, 0, 0.08);
          border-color: #dddde1;
        }

        .sister-card--empty {
          border-style: dashed;
          box-shadow: none;
          background: #fafafa;
        }
        .sister-card--empty:hover {
          box-shadow: none;
          border-color: var(--vibe-color-primary);
        }

        .sister-card-media {
          position: relative;
          aspect-ratio: 4 / 3;
          background: #e8e8ea;
          overflow: hidden;
        }

        .sister-card-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .sister-card-image-placeholder {
          width: 100%;
          height: 100%;
          background:
            linear-gradient(135deg, #ececef 0%, #e2e3e6 50%, #ececef 100%);
        }

        .sister-card-body {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          padding: 1.25rem 1.35rem 1.5rem;
          flex: 1;
        }

        .sister-card-name {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          font-weight: 700;
          line-height: 1.3;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: var(--vibe-color-text);
          margin: 0;
        }

        .sister-card-location {
          font-family: var(--vibe-font-body);
          font-size: 0.9rem;
          line-height: 1.5;
          color: var(--vibe-color-text-muted);
          margin: 0;
        }

        .sister-card-location--link {
          display: block;
          color: var(--vibe-color-text-muted);
          text-decoration: none;
        }
        .sister-card-location--link:hover {
          color: var(--vibe-color-primary);
          text-decoration: underline;
        }

        .sister-card-hint {
          font-style: italic;
        }

        .sister-card-link {
          display: inline-flex;
          align-items: center;
          margin-top: auto;
          padding-top: 0.85rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.8rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--vibe-color-primary);
          text-decoration: none;
        }
        .sister-card-link:hover {
          text-decoration: underline;
        }
        .sister-card-link--muted {
          color: var(--vibe-color-text-muted);
          font-style: italic;
          text-transform: none;
          letter-spacing: 0;
          font-family: var(--vibe-font-body);
          font-weight: 400;
          font-size: 0.85rem;
        }

        .sister-properties-editor-note {
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.85rem;
          line-height: 1.6;
          color: var(--vibe-color-text-muted);
          margin: 1.75rem 0 0;
          font-style: italic;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'SisterProperties',
  displayName: 'Sister Properties',
  tokens: {},
  slotGroups: {
    'sisterproperties.community1': community1Def,
    'sisterproperties.community2': community2Def,
    'sisterproperties.community3': community3Def,
    'sisterproperties.community4': community4Def,
    'sisterproperties.community5': community5Def,
    'sisterproperties.community6': community6Def,
  },
};

(SisterProperties as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default SisterProperties;
